import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

export interface CloudWatchAlert {
  id: string;
  name: string;
  status: 'ALARM' | 'OK' | 'INSUFFICIENT_DATA';
  severity: 'high' | 'medium' | 'low';
  description: string;
  region: string;
  metricName: string;
  namespace: string;
  threshold: number;
  comparisonOperator: string;
  evaluationPeriods: number;
  period: number;
  statistic: string;
  treatMissingData: string;
  stateChangeTime: string;
  stateReason: string;
  tags: Array<{ Key: string; Value: string }>;
  metadata: {
    alarmArn: string;
    actionsEnabled: boolean;
    alarmActions: string[];
    insufficientDataActions: string[];
    okActions: string[];
    dimensions: Array<{ Name: string; Value: string }>;
  };
}

export class CloudWatchService {
  private static instance: CloudWatchService;

  static getInstance(): CloudWatchService {
    if (!CloudWatchService.instance) {
      CloudWatchService.instance = new CloudWatchService();
    }
    return CloudWatchService.instance;
  }

  private async apiRequest<T>(endpoint: string): Promise<T> {
    try {
      const token = localStorage.getItem('auth_token');
      
      if (!token) {
        throw new Error('Authentication token not found');
      }
      
      const url = `${API_BASE_URL}/api/infrastructure/aws${endpoint}`;
      
      console.log(`CloudWatch API Request: ${url}`);
      
      const response = await axios.get(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        timeout: 30000,
        withCredentials: false
      });
      
      console.log(`CloudWatch API Success for ${endpoint}:`, response.status);
      return response.data;
    } catch (error: any) {
      console.error(`CloudWatch API Error for ${endpoint}:`, {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        message: error.message
      });
      throw new Error(error.response?.data?.message || error.message || 'Failed to fetch CloudWatch data');
    }
  }

  async getAlerts(region?: string, accountId?: string): Promise<CloudWatchAlert[]> {
    const params = new URLSearchParams();
    if (region) params.append('region', region);
    if (accountId) params.append('accountId', accountId);

    const response = await this.apiRequest<any>(`/cloudwatch/alerts?${params.toString()}`);
    return response.success && response.data ? response.data : [];
  }

  async getAllAlerts(): Promise<{
    alerts: CloudWatchAlert[];
    summary: {
      total: number;
      byStatus: Record<string, number>;
      bySeverity: Record<string, number>;
      byRegion: Record<string, number>;
    };
  }> {
    try {
      const alerts = await this.getAlerts();
      
      const summary = {
        total: alerts.length,
        byStatus: {} as Record<string, number>,
        bySeverity: {} as Record<string, number>,
        byRegion: {} as Record<string, number>
      };

      alerts.forEach(alert => {
        // Count by status
        summary.byStatus[alert.status] = (summary.byStatus[alert.status] || 0) + 1;
        
        // Count by severity
        summary.bySeverity[alert.severity] = (summary.bySeverity[alert.severity] || 0) + 1;
        
        // Count by region
        summary.byRegion[alert.region] = (summary.byRegion[alert.region] || 0) + 1;
      });

      return { alerts, summary };
    } catch (error) {
      console.error('Failed to fetch CloudWatch alerts:', error);
      return {
        alerts: [],
        summary: {
          total: 0,
          byStatus: {},
          bySeverity: {},
          byRegion: {}
        }
      };
    }
  }
}

export const cloudWatchService = CloudWatchService.getInstance();